import deleteUserRoleApi from '@/api/delete/user-role/delete-user-role';
import getUserRoleApi from '@/api/get/user-management/get-user-role';
import updateUserRoleApi from '@/api/patch/user-role/update-user-role';
import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation, useQuery } from '@/hooks/use-api';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Styled from './user-role-style';
import UserRolesForm from './user-roles-form';

/**
 * Edit new user role
 * @returns {JSX.Element}
 */
const UserRolesEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: userRole, loading, error } = useQuery(getUserRoleApi, id);

  const [editUserRole, { loading: editUserRoleLoading }] =
    useMutation(updateUserRoleApi);

  const [deleteUserRole, { loading: deleteUserRoleLoading }] =
    useMutation(deleteUserRoleApi);

  const launchDeleteConfirmation = useCallback(() => {
    ConfirmationModal({
      title: 'Delete User Role',
      text: `Are you sure you want to delete ${editUserRole.name}?`,
      onConfirm: () =>
        deleteUserRole(id).then(() =>
          navigate('/app/user-management/user-roles'),
        ),
    });
  }, [deleteUserRole, editUserRole.name, id, navigate]);

  if (loading) {
    return <div> loading</div>;
  }

  if (error) {
    return <div> error</div>;
  }

  return (
    <Styled.Main>
      <FormWrapper
        defaultValues={async () => {
          const permissions = [];
          userRole.permissions.forEach((permissionData) => {
            const { feature_id: featureId, ...permission } = permissionData;
            if (
              permission.read &&
              permission.create &&
              permission.update &&
              permission.delete
            ) {
              permission.all = true;
            }
            permissions[featureId] = permission;
          });
          return { ...userRole, permissions };
        }}
        onSubmit={(values) => {
          const submitValues = { ...values };
          const perm = submitValues.permissions;
          const subPermissions = [];
          Object.keys(perm).forEach((key) => {
            delete perm[key].all;
            const permissionFound = userRole.permissions.find(
              (p) => p.feature_id === key,
            );
            if (permissionFound) {
              perm[key].id = permissionFound.id;
            }
            subPermissions.push({
              feature_id: key,
              ...perm[key],
            });
          });
          submitValues.permissions = subPermissions;
          editUserRole(id, submitValues).then(() => {
            navigate('/app/user-management/user-roles');
            Alert({
              icon: 'success',
              text: 'User role updated successfully',
            });
          });
        }}
      >
        <UserRolesForm type="edit" isLoading={loading} />
        <SubmitCard
          padding="0"
          primaryButtonTitle="Save"
          primaryButtonType="submit"
          primaryButtonDisabled={false}
          primaryButtonWidth="112px"
          isLoading={editUserRoleLoading || deleteUserRoleLoading || loading}
          secondaryButtonType="button"
          secondaryButtonDisabled={false}
          secondaryButtonWidth="112px"
          secondaryPermission={['userRole.delete']}
          submitClick={() => {}}
          submitClickSecondary={() => {
            launchDeleteConfirmation();
          }}
          secondaryButtonTitle="Delete"
        />
      </FormWrapper>
    </Styled.Main>
  );
};

UserRolesEdit.propTypes = {};

export default UserRolesEdit;
